import { api } from './api';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { DialogPanel, trapDialogFocus, PageState } from './ui';

export function NavigationDrawer({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (open) dialog?.showModal();
    else dialog?.close();
    const media = matchMedia('(min-width: 1136px)');
    const resize = () => {
      if (media.matches && open) onClose();
    };
    media.addEventListener('change', resize);
    return () => media.removeEventListener('change', resize);
  }, [open, onClose]);
  return (
    <dialog
      ref={ref}
      className="nav-drawer"
      onKeyDown={trapDialogFocus}
      aria-label="Yönetim menüsü"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="drawer-inner">
        <button className="drawer-close" onClick={onClose}>
          Menüyü kapat ✕
        </button>
        {children}
      </div>
    </dialog>
  );
}

export function CommandPalette({
  sections,
  onSelect,
  onClose,
  onOpenUser,
  onOpenAudit,
  allowUsers = true,
}: {
  sections: readonly (readonly [string, string])[];
  onSelect: (key: string) => void;
  onClose: () => void;
  allowUsers?: boolean;
  onOpenUser: (id: string) => void;
  onOpenAudit: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<'section' | 'user' | 'audit'>('section');
  const [users, setUsers] = useState<Array<{ userId: string; nickname: string }>>([]);
  const [audit, setAudit] = useState<{ id: string; action: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const request = useRef(0);
  useEffect(
    () => () => {
      ++request.current;
    },
    [],
  );
  const clear = () => {
    ++request.current;
    setUsers([]);
    setAudit(null);
    setError(null);
    setSearched(false);
    setBusy(false);
  };
  return (
    <DialogPanel label="Bölüme git" onClose={onClose}>
      <div className="palette-heading">
        <h2>Hızlı erişim</h2>
        <button onClick={onClose}>Kapat</button>
      </div>
      <div className="row" role="group" aria-label="Arama türü">
        {(['section', 'user', 'audit'] as const)
          .filter((m) => allowUsers || m !== 'user')
          .map((m) => (
            <button
              key={m}
              aria-pressed={mode === m}
              className={mode === m ? 'primary' : ''}
              onClick={() => {
                clear();
                setMode(m);
                setQuery('');
              }}
            >
              {{ section: 'Bölümler', user: 'Oyuncular', audit: 'İşlem kaydı' }[m]}
            </button>
          ))}
      </div>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (mode === 'section' || !query.trim()) return;
          const id = ++request.current;
          setBusy(true);
          setError(null);
          setUsers([]);
          setAudit(null);
          setSearched(true);
          try {
            if (mode === 'user') {
              const r = await api.users(query.trim());
              if (id === request.current) setUsers(r.users);
            } else {
              const r = await api.auditEntry(query.trim());
              if (id === request.current) setAudit(r.entry);
            }
          } catch (e) {
            if (id === request.current) setError(e instanceof Error ? e.message : String(e));
          } finally {
            if (id === request.current) setBusy(false);
          }
        }}
      >
        <label>
          {mode === 'section'
            ? 'Bölüm ara'
            : mode === 'user'
              ? 'Oyuncu adı veya kimliği'
              : 'İşlem kimliği'}
          <input
            autoFocus
            type="search"
            value={query}
            onChange={(e) => {
              clear();
              setQuery(e.target.value);
            }}
            required={mode !== 'section'}
          />
        </label>
        {mode !== 'section' && (
          <button className="primary" disabled={busy}>
            Ara
          </button>
        )}
      </form>
      <PageState
        loading={busy}
        error={error}
        empty={searched && !busy && !error && !users.length && !audit}
      />
      <div className="command-results">
        {mode === 'section' &&
          sections
            .filter(([, label]) =>
              label.toLocaleLowerCase('tr').includes(query.toLocaleLowerCase('tr')),
            )
            .map(([key, label]) => (
              <button
                key={key}
                onClick={() => {
                  onSelect(key);
                  onClose();
                }}
              >
                {label}
                <span aria-hidden="true">↗</span>
              </button>
            ))}
        {users.map((u) => (
          <button
            key={u.userId}
            onClick={() => {
              onOpenUser(u.userId);
              onClose();
            }}
          >
            {u.nickname}
            <small>{u.userId}</small>
          </button>
        ))}
        {audit && (
          <button
            onClick={() => {
              onOpenAudit(audit.id);
              onClose();
            }}
          >
            {audit.action}
            <small>{audit.id} ↗</small>
          </button>
        )}
      </div>
      {mode === 'user' && (
        <p className="muted small">Sunucuda arar · en fazla 30 sonuç. Aramanı daraltabilirsin.</p>
      )}
    </DialogPanel>
  );
}
